import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { ExternalLink, Loader2, Plus, Printer, X } from 'lucide-react';
import { Button } from '../../../../components/ui/Button';
import { Modal } from '../../../../components/ui/Modal';
import { DateRangePicker } from '../../../../components/ui/DateRangePicker';
import { FilterableSelect } from '../../../../components/ui/FilterableSelect';
import {
  fetchPickupCouriers,
  fetchPickupRequests,
  fetchEligibleOrders,
  assignOrdersToPickup,
  requestManifest,
} from '../../../../features/pickupRequests/pickupRequestsThunks';
import {
  setPickupFilters,
  clearPickupError,
  resetPickupRequests,
} from '../../../../features/pickupRequests/pickupRequestsSlice';
import { selectIsRunning } from '../../../../features/feedback/feedbackSlice';
import {
  PICKUP_STATUS,
  PICKUP_STATUS_LABELS,
  isPickupUsable,
  pickupWindowLabel,
} from '../../../../features/pickupRequests/pickupRequestsUtils';

const pad = (value) => String(value).padStart(2, '0');

/** `dd/MM/yyyy HH:mm` in local time, as the legacy printed it. */
const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return dateStr;
  return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

/** Date only (`YYYY-MM-DD`), without a timezone shift. */
const formatDateOnly = (dateStr) => {
  if (!dateStr) return '—';
  const [y, m, d] = String(dateStr).slice(0, 10).split('-');
  if (!y || !m || !d) return dateStr;
  return `${d}/${m}/${y}`;
};

/** `dd/mm/yyyy HH:MM - HH:MM`; just the day for a pickup with no hours on record. */
const scheduledLabel = (pickup) => {
  const day = pickup.scheduled_date || pickup.requested_date;
  const window = pickupWindowLabel(pickup);
  return day && window ? `${formatDateOnly(day)} ${window}` : formatDateOnly(day);
};

// The legacy's own labels for the statuses a pickup can collect.
const STATUS_LABELS = {
  created_on_courier: 'Creado en courier',
  fulfilled: 'Despachado',
  withdrawn: 'Retirado',
  in_transit: 'En tránsito',
  in_cd: 'En CD',
  dropped_at_hub: 'En hub',
  received_at_hub: 'Recibido en hub',
  on_the_way: 'En camino',
};

const th = 'px-4 py-3 text-xs font-semibold text-text-muted uppercase tracking-wider text-left';
const td = 'px-4 py-3 text-sm text-ninja-dark dark:text-white';
const pill = 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold';

const STATUS_PILL = {
  [PICKUP_STATUS.CREATED]: 'bg-ninja-green text-white',
  [PICKUP_STATUS.FAILED]: 'bg-red-50 dark:bg-red-900/20 text-danger border border-red-200 dark:border-red-900',
  [PICKUP_STATUS.PENDING]: 'bg-ninja-gray dark:bg-[#2d2d2d] text-text-secondary',
};

/** A failed request has no courier id; ours names it in labels. */
const pickupName = (pickup) => pickup.external_pickup_id || `#${pickup.id}`;

const PickupStatus = ({ pickup }) => (
  <div data-testid="pickup-request-status" data-status={pickup.status}>
    <span className={`${pill} ${STATUS_PILL[pickup.status] || STATUS_PILL[PICKUP_STATUS.PENDING]}`}>
      {PICKUP_STATUS_LABELS[pickup.status] || pickup.status}
    </span>
    {pickup.status === PICKUP_STATUS.FAILED && pickup.error_message && (
      <p className="text-xs text-danger mt-1 max-w-xs">{pickup.error_message}</p>
    )}
  </div>
);

/**
 * Associates orders with a pickup request and gets its manifest.
 *
 * Assigning calls the courier's API, and so does generating a manifest: both
 * act on the courier's side, not only on NinjaHubs.
 */
export const AssignToManifest = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { pickupRequests, eligibleOrders, availableCouriers, loading, error, filters } =
    useSelector((state) => state.pickupRequests);
  // Not `loading`: that one is also up while the lists reload, and the dialog
  // would say "Asignando..." with nothing being assigned.
  const assigning = useSelector(selectIsRunning(assignOrdersToPickup));

  const [selectedOrders, setSelectedOrders] = useState([]);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [selectedPickupForAssign, setSelectedPickupForAssign] = useState(null);
  const [manifestLoading, setManifestLoading] = useState(null);
  const selectAllRef = useRef(null);

  useEffect(() => {
    dispatch(fetchPickupCouriers());
    return () => {
      dispatch(resetPickupRequests());
    };
  }, [dispatch]);

  // Both lists follow the filters, as in the legacy.
  useEffect(() => {
    dispatch(fetchPickupRequests());
    dispatch(fetchEligibleOrders());
  }, [dispatch, filters]);

  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate =
        selectedOrders.length > 0 && selectedOrders.length < eligibleOrders.length;
    }
  }, [selectedOrders, eligibleOrders]);

  const handleCourierChange = (value) => {
    dispatch(setPickupFilters({ courier: value }));
    setSelectedOrders([]);
  };

  const handleSelectOrder = (orderId) => {
    setSelectedOrders((prev) =>
      prev.includes(orderId) ? prev.filter((id) => id !== orderId) : [...prev, orderId]
    );
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedOrders(eligibleOrders.map((order) => order.order_id));
    } else {
      setSelectedOrders([]);
    }
  };

  const handleAssignConfirm = async () => {
    if (!selectedPickupForAssign) return;
    const action = await dispatch(
      assignOrdersToPickup({ pickupId: selectedPickupForAssign, orderIds: selectedOrders })
    );

    setAssignDialogOpen(false);
    if (action.meta.requestStatus !== 'fulfilled') return;

    setSelectedOrders([]);
    setSelectedPickupForAssign(null);
    dispatch(fetchEligibleOrders());
  };

  const handleManifest = async (pickupId) => {
    setManifestLoading(pickupId);
    const action = await dispatch(requestManifest(pickupId));
    if (action.meta.requestStatus === 'fulfilled' && action.payload?.manifest_url) {
      window.open(action.payload.manifest_url, '_blank');
    }
    setManifestLoading(null);
  };

  const pickupsForAssignDialog = pickupRequests.filter(
    (pickup) => !filters.courier || pickup.courier === filters.courier
  );

  const courierOptions = [
    { value: '', label: 'Todos' },
    ...availableCouriers.map(({ courier }) => ({ value: courier, label: courier.toUpperCase() })),
  ];

  return (
    <div data-testid="pickup-assign-page" className="space-y-6">
      <div className="flex justify-end">
        <Button variant="primary" icon={Plus} onClick={() => navigate('/pickup-requests/create')}>
          Nueva solicitud de retiro
        </Button>
      </div>

      {error && (
        <div
          data-testid="pickup-error"
          className="flex items-center justify-between gap-4 bg-red-50 dark:bg-red-900/20 text-danger px-5 py-3 rounded-2xl border border-red-200 dark:border-red-900 text-sm font-medium"
        >
          <span>{error}</span>
          <button type="button" aria-label="Cerrar error" onClick={() => dispatch(clearPickupError())}>
            <X size={16} />
          </button>
        </div>
      )}

      <div data-testid="pickup-assign-filters" className="flex flex-col md:flex-row flex-wrap items-center gap-4">
        <FilterableSelect
          testId="pickup-courier-filter"
          label="Courier:"
          value={filters.courier}
          onChange={handleCourierChange}
          options={courierOptions}
          placeholder="Todos"
        />
        <DateRangePicker
          startDate={filters.date_from}
          endDate={filters.date_to}
          onChange={({ start_date, end_date }) =>
            dispatch(setPickupFilters({ date_from: start_date || '', date_to: end_date || '' }))
          }
        />
      </div>

      {/* Pickup requests */}
      <section className="space-y-3">
        <h2 className="text-base font-bold text-ninja-dark dark:text-white">Solicitudes de retiro</h2>
        {loading ? (
          <div className="flex justify-center py-6">
            <Loader2 size={24} className="text-ninja-green animate-spin" />
          </div>
        ) : (
          <div className="overflow-x-auto bg-white dark:bg-[#1a1a1a] rounded-2xl border border-border">
            <table data-testid="pickup-requests-table" className="w-full border-collapse">
              <thead className="bg-ninja-gray/50 dark:bg-[#222]">
                <tr>
                  <th className={th}>#</th>
                  <th className={th}>Courier</th>
                  <th className={th}>Estado</th>
                  <th className={th}>ID Recogida</th>
                  <th className={th}>Fecha creación</th>
                  <th className={th}>Fecha programada</th>
                  <th className={th}>Pedidos asignados</th>
                  <th className={th}>Manifiesto</th>
                </tr>
              </thead>
              <tbody>
                {pickupRequests.length === 0 && (
                  <tr>
                    <td colSpan={8} className={`${td} text-center text-text-secondary`}>
                      No hay solicitudes de retiro creadas.
                    </td>
                  </tr>
                )}
                {pickupRequests.map((pickup) => (
                  <tr key={pickup.id} data-testid="pickup-request-row" data-pickup-id={pickup.id} className="border-t border-border">
                    <td className={td}>{pickup.id}</td>
                    <td className={td}>
                      <span className={`${pill} border border-ninja-green text-ninja-green`}>
                        {pickup.courier?.toUpperCase()}
                      </span>
                    </td>
                    <td className={td}>
                      <PickupStatus pickup={pickup} />
                    </td>
                    <td className={td}>{pickup.external_pickup_id || '—'}</td>
                    <td className={td}>{formatDate(pickup.created_at)}</td>
                    <td className={td} data-testid="pickup-request-scheduled">
                      {scheduledLabel(pickup)}
                    </td>
                    <td className={td}>{pickup.assigned_orders_count ?? '—'}</td>
                    <td className={td}>
                      {!isPickupUsable(pickup) ? (
                        <Button
                          variant="subtle"
                          size="sm"
                          icon={Printer}
                          iconOnly
                          disabled
                          title="Solo disponible para solicitudes creadas"
                          aria-label={`Solicitar manifiesto de ${pickupName(pickup)}`}
                        />
                      ) : pickup.manifest_url ? (
                        <Button
                          variant="subtle"
                          size="sm"
                          icon={ExternalLink}
                          iconOnly
                          title="Abrir manifiesto"
                          aria-label={`Abrir manifiesto de ${pickup.external_pickup_id}`}
                          onClick={() => window.open(pickup.manifest_url, '_blank')}
                        />
                      ) : (
                        <Button
                          variant="subtle"
                          size="sm"
                          icon={Printer}
                          iconOnly
                          title="Solicitar manifiesto"
                          aria-label={`Solicitar manifiesto de ${pickup.external_pickup_id}`}
                          loading={manifestLoading === pickup.id}
                          disabled={manifestLoading === pickup.id}
                          onClick={() => handleManifest(pickup.id)}
                        />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <div className="h-px bg-border" />

      {/* Eligible orders */}
      <section className="space-y-3">
        <div className="flex items-center justify-between gap-3 min-h-[40px]">
          <h2 className="text-base font-bold text-ninja-dark dark:text-white flex items-center gap-2">
            Pedidos elegibles
            {selectedOrders.length > 0 && (
              <span className={`${pill} bg-ninja-green text-white`}>
                {selectedOrders.length} seleccionados
              </span>
            )}
          </h2>
          {selectedOrders.length > 0 && (
            <Button variant="primary" onClick={() => setAssignDialogOpen(true)}>
              Asignar a solicitud de retiro
            </Button>
          )}
        </div>

        <div className="overflow-x-auto bg-white dark:bg-[#1a1a1a] rounded-2xl border border-border">
          <table data-testid="eligible-orders-table" className="w-full border-collapse">
            <thead className="bg-ninja-gray/50 dark:bg-[#222]">
              <tr>
                <th className={th}>
                  <input
                    ref={selectAllRef}
                    type="checkbox"
                    aria-label="Seleccionar todos los pedidos"
                    checked={eligibleOrders.length > 0 && selectedOrders.length === eligibleOrders.length}
                    onChange={handleSelectAll}
                    disabled={eligibleOrders.length === 0}
                    className="w-4 h-4 accent-ninja-green"
                  />
                </th>
                <th className={th}>Orden</th>
                <th className={th}>Referencia</th>
                <th className={th}>Fecha creación</th>
                <th className={th}>Courier</th>
                <th className={th}>Tracking</th>
                <th className={th}>Etiqueta</th>
                <th className={th}>Estado</th>
                <th className={th}>Asignado</th>
              </tr>
            </thead>
            <tbody>
              {eligibleOrders.length === 0 && (
                <tr>
                  <td colSpan={9} className={`${td} text-center text-text-secondary`}>
                    No hay pedidos elegibles
                    {filters.courier ? ` para ${filters.courier.toUpperCase()}` : ''}.
                  </td>
                </tr>
              )}
              {eligibleOrders.map((order) => (
                <tr
                  key={order.order_id}
                  data-testid="eligible-order-row"
                  data-order-id={order.order_id}
                  className={`border-t border-border ${selectedOrders.includes(order.order_id) ? 'bg-ninja-greenLight/40' : ''}`}
                >
                  <td className={td}>
                    <input
                      type="checkbox"
                      aria-label={`Seleccionar pedido ${order.order_id}`}
                      checked={selectedOrders.includes(order.order_id)}
                      onChange={() => handleSelectOrder(order.order_id)}
                      disabled={order.assigned_to_pickup}
                      className="w-4 h-4 accent-ninja-green"
                    />
                  </td>
                  <td className={td}>{order.order_id}</td>
                  <td className={td}>{order.reference_id}</td>
                  <td className={td}>{formatDate(order.created_at)}</td>
                  <td className={td}>
                    <span className={`${pill} border border-border text-text-secondary`}>
                      {order.courier?.toUpperCase()}
                    </span>
                  </td>
                  <td className={td}>{order.tracking_number || '—'}</td>
                  <td className={td}>
                    {order.label_url ? (
                      <Button
                        variant="subtle"
                        size="sm"
                        icon={ExternalLink}
                        iconOnly
                        title="Ver etiqueta"
                        aria-label={`Ver etiqueta del pedido ${order.order_id}`}
                        onClick={() => window.open(order.label_url, '_blank')}
                      />
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className={td}>
                    <span className={`${pill} bg-ninja-gray dark:bg-[#2d2d2d] text-text-secondary`}>
                      {STATUS_LABELS[order.delivery_status] || order.delivery_status}
                    </span>
                  </td>
                  <td className={td}>
                    {order.assigned_to_pickup ? (
                      <span className={`${pill} bg-ninja-green text-white`}>
                        {order.assigned_pickup_id ? `Retiro ${order.assigned_pickup_id}` : 'Asignado'}
                      </span>
                    ) : (
                      <span className={`${pill} border border-border text-text-secondary`}>Pendiente</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <Modal
        isOpen={assignDialogOpen}
        onClose={() => setAssignDialogOpen(false)}
        title="Asignar pedidos a solicitud de retiro"
        testId="assign-pickup-modal"
        busy={assigning}
      >
        <div className="space-y-4">
          <p className="text-sm text-text-secondary">
            Selecciona la solicitud de retiro a la que quieres asignar los{' '}
            <strong className="text-ninja-dark dark:text-white">{selectedOrders.length}</strong> pedidos seleccionados.
          </p>

          {!pickupsForAssignDialog.some(isPickupUsable) && (
            <div className="bg-orange-50 dark:bg-orange-900/20 text-warning p-3 rounded-xl border border-orange-200 dark:border-orange-900 text-sm">
              No hay solicitudes de retiro disponibles. Crea una primero.
            </div>
          )}

          <div className="space-y-2 max-h-[40vh] overflow-y-auto">
            {pickupsForAssignDialog.map((pickup) => {
              // Only a pickup the courier accepted takes orders.
              const usable = isPickupUsable(pickup);
              return (
                <label
                  key={pickup.id}
                  data-testid="assign-pickup-option"
                  data-pickup-id={pickup.id}
                  data-status={pickup.status}
                  className={`flex items-center gap-3 p-3 rounded-xl border transition-colors ${
                    !usable
                      ? 'border-border opacity-60 cursor-not-allowed'
                      : selectedPickupForAssign === pickup.id
                        ? 'border-ninja-green bg-ninja-greenLight/40 cursor-pointer'
                        : 'border-border hover:bg-ninja-gray dark:hover:bg-[#2d2d2d] cursor-pointer'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={selectedPickupForAssign === pickup.id}
                    onChange={() => setSelectedPickupForAssign(pickup.id)}
                    disabled={!usable}
                    className="w-4 h-4 accent-ninja-green"
                  />
                  <span className="text-sm font-semibold text-ninja-dark dark:text-white">{pickupName(pickup)}</span>
                  <span className="text-sm text-text-secondary">{pickup.courier?.toUpperCase()}</span>
                  {!usable && (
                    <span className={`${pill} ${STATUS_PILL[pickup.status] || STATUS_PILL[PICKUP_STATUS.PENDING]}`}>
                      {PICKUP_STATUS_LABELS[pickup.status] || pickup.status}
                    </span>
                  )}
                  <span className="text-sm text-text-secondary ml-auto">
                    {formatDateOnly(pickup.scheduled_date || pickup.requested_date)}
                  </span>
                </label>
              );
            })}
          </div>

          <div className="flex items-center justify-end gap-3 pt-2">
            <Button variant="subtle" pill onClick={() => setAssignDialogOpen(false)} disabled={assigning}>
              Cancelar
            </Button>
            <Button
              variant="primary"
              pill
              disabled={!selectedPickupForAssign || loading}
              loading={assigning}
              loadingLabel="Asignando..."
              onClick={handleAssignConfirm}
            >
              Confirmar asignación
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};
